import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import GestionPedidoBase from '../shared/GestionPedidoBase';
import CambioMesaModal from '../../shared/modals/CambioMesaModal';
import SeleccionMeseroHorizontal from '../../shared/selectors/SeleccionMeseroHorizontal';
import ModalNumeroPersonas from '../../shared/modals/ModalNumeroPersonas';
import API from '../../../../utils/api';
import { useToast } from '../../../../contexts/ToastContext';

/**
 * Componente para gestionar pedidos de mesa usando el componente base
 */
const PedidoMesaDetalle = () => {
  const { idMesa } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Estados específicos para pedido de mesa
  const [datosPedido, setDatosPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mostrarModalMesa, setMostrarModalMesa] = useState(false);
  const [mostrarModalMesero, setMostrarModalMesero] = useState(false);
  const [mostrarModalPersonas, setMostrarModalPersonas] = useState(false);

  // Estados para controlar cambios recientes
  const [ultimoCambioPersonas, setUltimoCambioPersonas] = useState(null);
  const [ultimoCambioMesero, setUltimoCambioMesero] = useState(null);
  const [ultimoCambioMesa, setUltimoCambioMesa] = useState(null);
  const [ultimoCambioStatus, setUltimoCambioStatus] = useState(null);

  // Cargar datos del pedido desde navigation state o servidor
  useEffect(() => {
    const cargarDatosPedido = async () => {
      try {
        console.log('⏰ useEffect cargarDatosPedido ejecutándose...');
        console.log('⏰ ultimoCambioPersonas:', ultimoCambioPersonas);

        // Si ya tenemos un pedido cargado, refrescar su status desde el servidor
        // (cubre el caso de navigate(-1) desde la pantalla de pago)
        if (datosPedido?.idPedido) {
          console.log('🔄 Refrescando estado del pedido existente desde servidor:', datosPedido.idPedido);
          try {
            const pedidoActualizado = await API.pedidos.getPedido(datosPedido.idPedido);
            if (pedidoActualizado) {
              setDatosPedido(prev => ({
                ...prev,
                status: pedidoActualizado.status || prev.status,
                liquidado: pedidoActualizado.liquidado || prev.liquidado,
                numeroDia: pedidoActualizado.numeroDia || prev.numeroDia
              }));
              console.log('✅ Estado refrescado:', pedidoActualizado.status);
            }
          } catch (e) {
            console.error('Error al refrescar estado del pedido:', e);
          }
          setLoading(false);
          return;
        }

        // PROTECCION: No recargar si se hicieron cambios recientes (solo para carga inicial)
        const ahora = new Date();
        const tiempoDesdePersonas = ultimoCambioPersonas ? (ahora - ultimoCambioPersonas) : Infinity;
        const tiempoDesdeMesero = ultimoCambioMesero ? (ahora - ultimoCambioMesero) : Infinity;
        const tiempoDesdeMesa = ultimoCambioMesa ? (ahora - ultimoCambioMesa) : Infinity;
        const tiempoDesdeStatus = ultimoCambioStatus ? (ahora - ultimoCambioStatus) : Infinity;

        const haycambiosRecientes = tiempoDesdePersonas < 20000 || tiempoDesdeMesero < 20000 || tiempoDesdeMesa < 20000 || tiempoDesdeStatus < 30000;

        if (haycambiosRecientes) {
          console.log(`🚫 BLOQUEANDO useEffect por cambios recientes:`);
          console.log(`  - Personas: ${Math.round(tiempoDesdePersonas / 1000)}s`);
          console.log(`  - Mesero: ${Math.round(tiempoDesdeMesero / 1000)}s`);
          console.log(`  - Mesa: ${Math.round(tiempoDesdeMesa / 1000)}s`);
          console.log(`  - Status: ${Math.round(tiempoDesdeStatus / 1000)}s`);
          return;
        }

        // Primero intentar obtener datos del navigation state
        if (location.state?.datosPedido) {
          console.log('✅ Cargando desde navigation state');
          setDatosPedido(location.state.datosPedido);
          return; // Salir temprano si tenemos datos del state
        }

        // Si no hay datos en state, buscar en el servidor
        if (idMesa) {
          console.log('📡 Cargando desde servidor para mesa:', idMesa);
          // Verificar si hay un pedido activo en la mesa
          const pedidoActivo = await API.pedidos.verificarPedidoEnMesa(idMesa);
          if (pedidoActivo) {
            console.log('📦 Pedido activo encontrado:', pedidoActivo);
            setDatosPedido({
              idPedido: pedidoActivo.idPedido,
              numeroPedido: pedidoActivo.numeroDia,
              mesa: pedidoActivo.mesa,
              mesero: pedidoActivo.mesero,
              numeroPersonas: pedidoActivo.numeroPersonas,
              status: pedidoActivo.status
            });
          } else {
            // Si no hay pedido activo, obtener información básica de la mesa
            try {
              const mesaInfo = await API.mesas.getById(idMesa);
              setDatosPedido({
                idPedido: null,
                numeroPedido: 'NUEVO',
                mesa: mesaInfo,
                mesero: null,
                numeroPersonas: 1,
                status: 'nuevo'
              });
            } catch (error) {
              console.error("Error al obtener información de la mesa:", error);
              // Fallback con datos mínimos
              setDatosPedido({
                idPedido: null,
                numeroPedido: 'NUEVO',
                mesa: { idMesa: idMesa, caption: idMesa, numero: idMesa },
                mesero: null,
                numeroPersonas: 1,
                status: 'nuevo'
              });
            }
          }
        }
      } catch (error) {
        console.error("Error al cargar datos del pedido:", error);
        toast.error("Error al cargar información del pedido");
      } finally {
        setLoading(false);
      }
    };

    cargarDatosPedido();
  }, [idMesa, location, toast, ultimoCambioPersonas, ultimoCambioMesero, ultimoCambioMesa]);


  // Función para cambiar mesa
  const handleCambiarMesa = async (nuevaMesa) => {
    console.log('🔄 INICIO handleCambiarMesa con mesa:', nuevaMesa);
    console.log('🔍 Estado actual datosPedido:', datosPedido);

    try {
      if (datosPedido?.idPedido) {
        console.log('🎯 Llamando API para cambiar mesa del pedido:', datosPedido.idPedido);
        await API.pedidos.cambiarMesa(datosPedido.idPedido, nuevaMesa.idMesa);
        console.log('✅ API respondió exitosamente');
      }

      setDatosPedido(prev => {
        const nuevoDatos = {
          ...prev,
          mesa: nuevaMesa,
          numeroMesa: nuevaMesa.numero,
          nombreMesa: nuevaMesa.nombre
        };

        console.log('🎉 Mesa actualizada en PedidoMesaDetalle:', nuevoDatos);
        console.log('🔍 Comparación - Anterior:', prev.mesa, 'Nueva:', nuevoDatos.mesa);
        setUltimoCambioMesa(new Date());
        return nuevoDatos;
      });

      setMostrarModalMesa(false);
      toast.success(`Mesa cambiada a ${nuevaMesa.nombre || nuevaMesa.numero}`);
    } catch (error) {
      console.error("❌ Error al cambiar mesa:", error);
      toast.error("Error al cambiar la mesa");
    }
  };

  // Función para cambiar mesero
  const handleCambiarMesero = async (nuevoMesero) => {
    try {
      if (datosPedido?.idPedido) {
        // Usar endpoint específico para cambiar solo el mesero
        await API.pedidos.cambiarMesero(datosPedido.idPedido, nuevoMesero.nombre);

        // Recargar información del pedido desde la BD para asegurar consistencia
        try {
          const pedidoActualizado = await API.pedidos.getPedido(datosPedido.idPedido);
          if (pedidoActualizado && pedidoActualizado.mesero) {
            // Mantener la estructura del objeto mesero pero con el nombre actualizado desde BD
            setDatosPedido(prev => {
              const meseroActualizado = {
                ...nuevoMesero,
                nombre: pedidoActualizado.mesero
              };
              const nuevoDatos = {
                ...prev,
                mesero: meseroActualizado
              };

              console.log('👨‍🍳 Mesero actualizado en PedidoMesaDetalle:', nuevoDatos);
              setUltimoCambioMesero(new Date());
              return nuevoDatos;
            });
          } else {
            // Si no se puede recargar, usar el valor enviado
            setDatosPedido(prev => {
              const nuevoDatos = {
                ...prev,
                mesero: nuevoMesero
              };

              console.log('👨‍🍳 Mesero actualizado en PedidoMesaDetalle (fallback):', nuevoDatos);
              setUltimoCambioMesero(new Date());
              return nuevoDatos;
            });
          }
        } catch (reloadError) {
          console.error("Error al recargar pedido:", reloadError);
          // Usar el valor enviado como fallback
          setDatosPedido(prev => {
            const nuevoDatos = {
              ...prev,
              mesero: nuevoMesero
            };

            console.log('👨‍🍳 Mesero actualizado en PedidoMesaDetalle (error fallback):', nuevoDatos);
            setUltimoCambioMesero(new Date());
            return nuevoDatos;
          });
        }
      } else {
        // Si no hay idPedido, actualizar solo el estado local
        setDatosPedido(prev => {
          const nuevoDatos = {
            ...prev,
            mesero: nuevoMesero
          };
          console.log('👨‍🍳 Mesero actualizado en PedidoMesaDetalle (sin pedido):', nuevoDatos);
          setUltimoCambioMesero(new Date());
          return nuevoDatos;
        });
      }

      setMostrarModalMesero(false);
      toast.success(`Mesero cambiado a ${nuevoMesero.nombre}`);
    } catch (error) {
      console.error("Error al cambiar mesero:", error);
      toast.error("Error al cambiar el mesero");
    }
  };

  // Función para abrir modal de cambio de personas
  const handleCambiarPersonas = () => {
    setMostrarModalPersonas(true);
  };

  // Función para confirmar cambio de personas
  const handleConfirmarPersonas = async (numeroPersonas) => {
    console.log('🔄 INICIO handleConfirmarPersonas con valor:', numeroPersonas);
    console.log('🔍 Estado actual datosPedido:', datosPedido);

    try {
      if (datosPedido?.idPedido) {
        console.log('🎯 Llamando API para cambiar personas del pedido:', datosPedido.idPedido);
        // Usar endpoint específico para cambiar solo el número de personas
        const respuestaAPI = await API.pedidos.cambiarNumeroPersonas(datosPedido.idPedido, numeroPersonas);
        console.log('✅ API respondió exitosamente:', respuestaAPI);

        // Primero actualizar inmediatamente con el valor que sabemos que se envió
        setDatosPedido(prev => {
          const nuevoDatos = {
            ...prev,
            numeroPersonas: numeroPersonas
          };
          console.log('⚡ Actualización inmediata del número de personas:', nuevoDatos.numeroPersonas);
          setUltimoCambioPersonas(new Date());
          return nuevoDatos;
        });

        // Luego, opcionalmente, verificar con la BD (con delay para asegurar consistencia)
        setTimeout(async () => {
          try {
            console.log('🔄 Verificando consistencia con BD...');
            const pedidoActualizado = await API.pedidos.getPedido(datosPedido.idPedido);
            console.log('📦 Pedido verificado desde BD:', pedidoActualizado);

            if (pedidoActualizado && pedidoActualizado.numeroPersonas !== undefined) {
              console.log('✅ Número de personas en BD:', pedidoActualizado.numeroPersonas);

              // Solo actualizar si hay diferencia
              if (pedidoActualizado.numeroPersonas !== numeroPersonas) {
                console.log('⚠️ Discrepancia detectada, ajustando valor');
                setDatosPedido(prev => ({
                  ...prev,
                  numeroPersonas: pedidoActualizado.numeroPersonas
                }));
              } else {
                console.log('✅ Valores consistentes, no se requiere ajuste');
              }
            }
          } catch (reloadError) {
            console.error("⚠️ Error al verificar consistencia:", reloadError);
            // No hacer nada, mantener el valor que ya se había establecido
          }
        }, 1000); // Verificar después de 1 segundo
      } else {
        // Si no hay idPedido, actualizar solo el estado local
        console.log('⚠️ No hay idPedido, actualizando solo estado local');
        setDatosPedido(prev => {
          const nuevoDatos = {
            ...prev,
            numeroPersonas: numeroPersonas
          };
          console.log('👥 Número de personas actualizado en PedidoMesaDetalle (sin pedido):', nuevoDatos);
          setUltimoCambioPersonas(new Date());
          return nuevoDatos;
        });
      }

      toast.success(`Número de personas actualizado a ${numeroPersonas}`);
      setMostrarModalPersonas(false);
    } catch (error) {
      console.error("❌ Error al cambiar número de personas:", error);
      toast.error("Error al actualizar el número de personas");
    }
  };

  // Función para cancelar cambio de personas
  const handleCancelarPersonas = () => {
    setMostrarModalPersonas(false);
  };

  // Callbacks para eventos del pedido
  const handlePedidoCreado = (nuevoPedido) => {
    console.log("Pedido creado:", nuevoPedido);
    // Actualizar el estado local cuando se crea un pedido
    if (nuevoPedido && nuevoPedido.idPedido) {
      setDatosPedido(prev => ({
        ...prev,
        idPedido: nuevoPedido.idPedido,
        numeroPedido: nuevoPedido.numeroPedido || nuevoPedido.numeroDia,
        status: nuevoPedido.status || 'abierto'
      }));
    }
  };

  const handlePedidoActualizado = (pedidoActualizado) => {
    console.log("🔄 Pedido actualizado desde GestionPedidoBase:", pedidoActualizado);
    console.log("🕵️ Trace del callback:", new Error().stack);

    // No actualizar si no hay datos
    if (!pedidoActualizado) {
      console.log("⚠️ No hay datos, saltando callback");
      return;
    }

    const ahora = new Date();

    // Preservar numero de personas si se cambió recientemente (menos de 15 segundos)
    const tiempoDesdePersonas = ultimoCambioPersonas ? (ahora - ultimoCambioPersonas) : Infinity;
    if (tiempoDesdePersonas < 15000) {
      console.log(`🚫 BLOQUEANDO callback completo por cambio reciente de personas (${Math.round(tiempoDesdePersonas / 1000)}s)`);
      console.log(`🚫 Datos que se iban a aplicar:`, pedidoActualizado);
      return; // Bloquear completamente el callback
    }

    // DETECTAR si es un cambio de mesa desde GestionPedidoBase
    const esCambioMesa = pedidoActualizado.mesa && JSON.stringify(pedidoActualizado.mesa) !== JSON.stringify(datosPedido?.mesa);
    if (esCambioMesa) {
      console.log('🎉 Detectado cambio de mesa desde GestionPedidoBase, estableciendo timestamp');
      setUltimoCambioMesa(new Date());
    }

    // DETECTAR si es un cambio de status (finalización)
    const esCambioStatus = pedidoActualizado.status && pedidoActualizado.status !== datosPedido?.status;
    if (esCambioStatus) {
      console.log(`🎉 DETECTADO CAMBIO DE STATUS: ${datosPedido?.status} → ${pedidoActualizado.status}`);
      setUltimoCambioStatus(new Date());
    }

    setDatosPedido(prev => {
      const newState = { ...prev };

      // Preservar mesero si se cambió recientemente (menos de 10 segundos)
      const tiempoDesdeMesero = ultimoCambioMesero ? (ahora - ultimoCambioMesero) : Infinity;
      if (tiempoDesdeMesero < 10000) {
        console.log(`🚫 Preservando mesero por cambio reciente (${Math.round(tiempoDesdeMesero / 1000)}s)`);
        // No actualizar mesero, mantener el valor local
        delete pedidoActualizado.mesero;
      }

      // NO preservar mesa si viene un cambio desde GestionPedidoBase (es intencional)
      if (!esCambioMesa) {
        const tiempoDesdeMesa = ultimoCambioMesa ? (ahora - ultimoCambioMesa) : Infinity;
        if (tiempoDesdeMesa < 10000) {
          console.log(`🚫 Preservando mesa por cambio reciente (${Math.round(tiempoDesdeMesa / 1000)}s)`);
          // No actualizar mesa, mantener el valor local
          delete pedidoActualizado.mesa;
        }
      }

      // Fusionar el estado con los datos actualizados (excluyendo los campos protegidos)
      const updatedState = {
        ...prev,
        ...pedidoActualizado
      };

      console.log('🔄 Estado actualizado con protección:', updatedState);
      return updatedState;
    });
  };

  const handlePedidoCerrado = (resultado) => {
    console.log("Pedido cerrado:", resultado);
    // Actualizar el estado cuando se cierra el pedido
    if (resultado) {
      setDatosPedido(prev => ({
        ...prev,
        status: 'pendiente'
      }));
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Cargando información del pedido...</div>
      </div>
    );
  }

  if (!datosPedido) {
    return (
      <div className="loading-container">
        <div className="loading">Error: No se pudo cargar la información del pedido</div>
      </div>
    );
  }

  // Preparar datos extendidos del pedido con callbacks para edición
  const datosPedidoExtendido = {
    ...datosPedido,
    // Normalizar el mesero para evitar errores de renderizado
    mesero: datosPedido?.mesero ?
      (typeof datosPedido.mesero === 'object' ?
        (datosPedido.mesero.nombre || 'Sin asignar') :
        datosPedido.mesero) :
      null,
    // Guardar el objeto mesero completo para el selector
    meseroCompleto: datosPedido?.mesero && typeof datosPedido.mesero === 'object' ?
      datosPedido.mesero : null,
    // Callbacks para edición en línea
    onEditarMesa: () => setMostrarModalMesa(true),
    onEditarMesero: () => setMostrarModalMesero(true),
    onEditarPersonas: handleCambiarPersonas,
    // Forzar actualización con timestamp para que GestionPedidoBase detecte cambios
    ultimaActualizacion: Date.now()
  };

  console.log('🎨 RENDERIZANDO PedidoMesaDetalle');
  console.log('📦 datosPedido actual:', datosPedido);
  console.log('📝 datosPedidoExtendido:', datosPedidoExtendido);
  console.log('👥 Número de personas en datosPedidoExtendido:', datosPedidoExtendido.numeroPersonas);
  console.log('🎉 Mesa en datosPedidoExtendido:', datosPedidoExtendido.mesa);


  return (
    <>
      <GestionPedidoBase
        tipoPedido="mesa"
        datosPedido={datosPedidoExtendido}
        configuracion={{
          permitirCambioMesa: true,
          permitirCambioMesero: true,
          permitirCambioPersonas: true,
          permitirDatosCliente: false,
          permitirDireccion: false,
          permitirRepartidor: false,
          requiereImpresion: true,
          permitirPagoMultiple: true
        }}
        onPedidoCreado={handlePedidoCreado}
        onPedidoActualizado={handlePedidoActualizado}
        onPedidoCerrado={handlePedidoCerrado}
        rutaRegreso="/ventas"
      />

      {/* Modal para cambio de mesa */}
      {mostrarModalMesa && (
        <CambioMesaModal
          mesaActual={datosPedido?.mesa}
          onConfirmar={handleCambiarMesa}
          onCancelar={() => setMostrarModalMesa(false)}
        />
      )}

      {/* Modal para cambio de mesero */}
      {mostrarModalMesero && (
        <SeleccionMeseroHorizontal
          meseroActual={datosPedidoExtendido.meseroCompleto}
          onMeseroSelect={handleCambiarMesero}
          onCancel={() => setMostrarModalMesero(false)}
        />
      )}

      {/* Modal de Número de Personas */}
      <ModalNumeroPersonas
        isOpen={mostrarModalPersonas}
        onConfirm={handleConfirmarPersonas}
        onCancel={handleCancelarPersonas}
        initialValue={datosPedido?.numeroPersonas || 0}
        title="Número de Personas"
        label="Personas en la mesa"
      />
    </>
  );
};

export default PedidoMesaDetalle;